import React from 'react'
import {
  FaArrowAltCircleRight,
  FaArrowAltCircleLeft,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaCheck,
  FaPlus,
} from 'react-icons/fa'
import { parseMilitaryTimeToAMPM } from '@utils'
import { formatCutoff } from '@utils/groupOrderUtils'
import { FaCircleXmark } from 'react-icons/fa6'
import { SelectInput } from '@components/common/form'
import { MenuConcept } from './types'
import { MenuTable } from './MenuTable'
import { getAvailableDaysStr, getDietaryCountsArray } from './utils'
import { DietCountsCell, MenuItemsSubtable } from './MenuTable'

interface LegacyMenuSelectorProps {
  collapseAll: (set: React.Dispatch<React.SetStateAction<Set<string>>>) => void
  dietaryPreferenceOptions: { value: string; text: string }[]
  excludeDietaryPreferenceOptions: { value: string; text: string }[]
  expandedLegacy: Set<string>
  expandAll: (
    set: React.Dispatch<React.SetStateAction<Set<string>>>,
    menus: MenuConcept[],
  ) => void
  filterExcludeTags: boolean
  filteredDietaryPreference: string
  filteredExcludeTagsDietaryPreferenceUnique: string
  handleSearchSubmit: () => void
  handleStageMenu: (m: MenuConcept) => void
  isLoading: boolean
  limit: number
  menuConcepts: MenuConcept[]
  page: number
  performSearch: (
    search: string,
    pageNum: number,
    includeDietaryTag: string,
    excludeDietaryTag: string,
  ) => void
  search: string
  setExpandedLegacy: React.Dispatch<React.SetStateAction<Set<string>>>
  setFilterExcludeTags: (v: boolean) => void
  setFilteredDietaryPreference: (v: string) => void
  setFilteredExcludeTagsDietaryPreferenceUnique: (v: string) => void
  setPage: (v: number) => void
  setSearch: (v: string) => void
  setShowDietaryCounts: (v: boolean) => void
  showDietaryCounts: boolean
  stagedMenus: MenuConcept[]
  toggleExpanded: (
    set: React.Dispatch<React.SetStateAction<Set<string>>>,
  ) => (id: string) => void
  updateDietaryPreferenceFilters: (tag: string) => void
}

export const LegacyMenuSelector: React.FC<LegacyMenuSelectorProps> = ({
  dietaryPreferenceOptions,
  filterExcludeTags,
  filteredDietaryPreference,
  filteredExcludeTagsDietaryPreferenceUnique,
  excludeDietaryPreferenceOptions,
  isLoading,
  limit,
  menuConcepts,
  page,
  search,
  showDietaryCounts,
  stagedMenus,
  expandedLegacy,
  collapseAll,
  expandAll,
  performSearch,
  setExpandedLegacy,
  setFilterExcludeTags,
  setFilteredDietaryPreference,
  setFilteredExcludeTagsDietaryPreferenceUnique,
  setPage,
  setSearch,
  setShowDietaryCounts,
  updateDietaryPreferenceFilters,
  handleSearchSubmit,
  handleStageMenu,
  toggleExpanded,
}) => {
  const renderLegacyMenuRow = (menu: MenuConcept) => {
    const isOpen = expandedLegacy.has(menu.id)
    const onToggle = () => toggleExpanded(setExpandedLegacy)(menu.id)
    const isStaged = Boolean(
      stagedMenus.find((staged) => staged.id === menu.id),
    )

    return (
      <>
        <tr
          key={menu.id}
          className="border-b border-t border-gray-400 border-solid bg-gray-100 hover:bg-gray-300"
        >
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            <div className="flex items-center space-x-2">
              <button
                onClick={onToggle}
                className="focus:outline-none focus:ring-2 focus:ring-teal-500"
                aria-expanded={isOpen}
                title={isOpen ? 'Collapse items' : 'Expand items'}
              >
                {isOpen ? (
                  <FaChevronUp className="h-4 w-4" />
                ) : (
                  <FaChevronDown className="h-4 w-4" />
                )}
              </button>
              <span>{menu.name}</span>
              <button
                onClick={() => handleStageMenu(menu)}
                className={`ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold
                  ${
                    isStaged
                      ? 'bg-gray-500 text-white hover:bg-gray-700'
                      : 'bg-teal-500 text-white hover:bg-teal-700'
                  }
                  focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-teal-500`}
                aria-pressed={isStaged}
                aria-label={isStaged ? 'Unselect menu' : 'Select menu'}
                title={isStaged ? 'Selected' : 'Select'}
              >
                {isStaged ? (
                  <FaCheck className="h-3 w-3" />
                ) : (
                  <FaPlus className="h-3 w-3" />
                )}
                {isStaged ? 'Selected' : 'Add'}
              </button>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.chef.fullName || menu.chef.name} {/** REMOVE */}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            <div className="flex gap-1 flex-wrap">
              {menu.cuisines.map((tag: string) => (
                <div
                  key={tag}
                  className="p-1 rounded-lg bg-gray-200 flex items-center"
                >
                  {tag}
                </div>
              ))}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.hasMenuItemWithMods ? (
              <FaCheckCircle color={'green'} size={20} />
            ) : (
              <FaCircleXmark color="red" size={20} />
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {getAvailableDaysStr(menu)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {formatCutoff(menu.cutoffDays, menu.cutoffHours)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {!menu.pickupHours
              ? 'N/A'
              : parseMilitaryTimeToAMPM(menu.pickupHours)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.maxItems || 'N/A'}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.menuItems.length}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {menu.minGoPayout || 'N/A'}
          </td>
          <td className="px-6 py-4 text-sm text-gray-900 flex flex-col">
            <DietCountsCell
              tags={getDietaryCountsArray(menu)}
              showAllTags={showDietaryCounts}
            />
          </td>
        </tr>
        {isOpen && (
          <tr className="bg-white border-b border-solid border-gray-200">
            <td colSpan={11} className="px-6 py-3">
              <MenuItemsSubtable items={menu.menuItems} />
            </td>
          </tr>
        )}
      </>
    )
  }

  return (
    <div className="space-y-6 w-full">
      {/** Top Section - Search / Pagination Controls / Dietary Filtering */}
      <div className="bg-white rounded-lg shadow-md border p-4 mb-4">
        <div className="p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by menu name, chef, or cuisine"
              className="flex-1 border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
            />
            <button
              onClick={handleSearchSubmit}
              disabled={isLoading}
              className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:bg-gray-400 transition-colors whitespace-nowrap"
            >
              {isLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Pagination Controls */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {page > 0 && (
                  <button
                    className="p-2 rounded-md border border-gray-600 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    disabled={page <= 0}
                    onClick={() => {
                      const newPage = page - 1
                      setPage(newPage)
                      performSearch(
                        search,
                        newPage,
                        filteredDietaryPreference,
                        filteredExcludeTagsDietaryPreferenceUnique,
                      )
                    }}
                    title="Previous page"
                  >
                    <FaArrowAltCircleLeft size={18} />
                  </button>
                )}
                <span className="px-3 py-1 bg-gray-200 rounded-md font-medium text-gray-700 min-w-[80px] text-center">
                  Page {page + 1}
                </span>
                {menuConcepts.length === limit && (
                  <button
                    className="p-2 rounded-md border border-gray-600 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={() => {
                      const newPage = page + 1
                      setPage(newPage)
                      performSearch(
                        search,
                        newPage,
                        filteredDietaryPreference,
                        filteredExcludeTagsDietaryPreferenceUnique,
                      )
                    }}
                    title="Next page"
                  >
                    <FaArrowAltCircleRight size={18} />
                  </button>
                )}
              </div>

              {/* Results Count */}
              {menuConcepts.length > 0 && (
                <div className="text-sm text-gray-600">
                  Showing {menuConcepts.length} menu
                  {menuConcepts.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            {/* Filters Section */}
            <div className="flex flex-col sm:flex-row items-end gap-3">
              <SelectInput
                label="Dietary Preference"
                value={filterExcludeTags ? 'exclude' : 'include'}
                options={[
                  { value: 'include', text: 'Include' },
                  { value: 'exclude', text: 'Exclude' },
                ]}
                onChange={(nextDietaryExclude: string) => {
                  if (nextDietaryExclude === 'exclude') {
                    setFilterExcludeTags(true)
                    setFilteredDietaryPreference('')
                  } else {
                    setFilterExcludeTags(false)
                    setFilteredExcludeTagsDietaryPreferenceUnique('')
                  }
                }}
              />

              <div className="flex-shrink-0">
                <SelectInput
                  value={
                    filterExcludeTags
                      ? filteredExcludeTagsDietaryPreferenceUnique
                      : filteredDietaryPreference
                  }
                  options={
                    filterExcludeTags
                      ? excludeDietaryPreferenceOptions
                      : dietaryPreferenceOptions
                  }
                  onChange={updateDietaryPreferenceFilters}
                  excludeDefaultOption={true}
                />
              </div>

              <button
                onClick={() => setShowDietaryCounts(!showDietaryCounts)}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-md hover:bg-gray-200 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 transition-colors whitespace-nowrap flex items-center gap-2"
                title={
                  showDietaryCounts
                    ? 'Collapse dietary counts in table'
                    : 'Expand dietary counts in table'
                }
              >
                {showDietaryCounts ? (
                  <FaChevronUp size={12} />
                ) : (
                  <FaChevronDown size={12} />
                )}
                {showDietaryCounts ? 'Collapse Dietary' : 'Expand Dietary'}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <button
              className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold focus:outline-none"
              onClick={() => expandAll(setExpandedLegacy, menuConcepts)}
            >
              <FaChevronDown className="h-4 w-4 mx-1" />
              Expand All Items
            </button>
            <button
              className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold focus:outline-none"
              onClick={() => collapseAll(setExpandedLegacy)}
            >
              <FaChevronUp className="h-4 w-4 mx-1" />
              Collapse All Items
            </button>
          </div>
        </div>
      </div>

      <MenuTable
        menus={menuConcepts}
        isLoading={isLoading}
        renderRow={renderLegacyMenuRow}
      />
    </div>
  )
}
